/**
 * Template configuration constants for expanded rows
 */
import type { TemplateConfigField } from './types'

// Templates that use capacity_value * commitment_value for quota calculation
export const CAPACITY_COMMITMENT_TEMPLATES = ['Server.VBM', 'Server.Colocation']

// Addon statuses to exclude from summary calculation
export const EXCLUDED_ADDON_STATUSES = [
    'pending-terminated',
    'rejected',
    'cancelled',
]

/**
 * Check if addon should be included in summary calculation
 */
export const isAddonActiveForSummary = (addon: any): boolean => {
    const status = addon?.addon_service_status?.toLowerCase()
    return !EXCLUDED_ADDON_STATUSES.includes(status)
}

// ============================================================================
// Template-specific field configurations
// ============================================================================

export const TEMPLATE_SERVER_VBM: TemplateConfigField[] = [
    {
        key: 'server',
        names: ['server', 'processor'],
        label: 'Server/Processor',
    },
    { key: 'cpu', names: ['cpu', 'vcore', 'vcpu'], label: 'CPU' },
    { key: 'memory', names: ['memory', 'ram'], label: 'Memory' },
    { key: 'disk', names: ['disk', 'ssd', 'hdd', 'storage'], label: 'Disk' },
    { key: 'ip', names: ['ip', 'ip-address', 'ipv4', 'ipv6'], label: 'IP' },
    { key: 'bandwidth', names: ['bandwidth', 'bw'], label: 'Bandwidth' },
    {
        key: 'peak_bandwidth',
        names: ['peak-bandwidth', 'peak_bandwidth', 'peak-bw'],
        label: 'Peak BW',
    },
]

export const TEMPLATE_SERVER_COLOCATION: TemplateConfigField[] = [
    { key: 'u', names: ['u', 'colocation-u'], label: 'U' },
    { key: 'power', names: ['power', 'nguon'], label: 'Power' },
    { key: 'bandwidth', names: ['bandwidth', 'bw'], label: 'Bandwidth' },
    { key: 'local', names: ['local'], label: 'Local BW' },
    { key: 'global', names: ['global'], label: 'Global BW' },
    { key: 'ip', names: ['ip', 'ip-address', 'ipv4', 'ipv6'], label: 'IP' },
]

export const TEMPLATE_CLOUD_VPS: TemplateConfigField[] = [
    { key: 'cpu', names: ['cpu', 'vcore', 'vcpu'], label: 'CPU' },
    { key: 'memory', names: ['memory', 'ram'], label: 'Memory' },
    { key: 'disk', names: ['disk', 'ssd', 'hdd', 'storage'], label: 'Disk' },
    { key: 'ip', names: ['ip', 'ip-address', 'ipv4', 'ipv6'], label: 'IP' },
    { key: 'bandwidth', names: ['bandwidth', 'bw'], label: 'Bandwidth' },
]

export const TEMPLATE_CLOUD_VCLOUD: TemplateConfigField[] = [
    { key: 'cpu', names: ['cpu', 'vcore', 'vcpu'], label: 'CPU' },
    { key: 'memory', names: ['memory', 'ram'], label: 'Memory' },
    { key: 'disk', names: ['disk', 'ssd', 'hdd', 'storage'], label: 'Disk' },
    { key: 'ip', names: ['ip', 'ip-address', 'ipv4', 'ipv6'], label: 'IP' },
    { key: 'bandwidth', names: ['bandwidth', 'bw'], label: 'Bandwidth' },
]

export const TEMPLATE_CLOUD_HOSTING: TemplateConfigField[] = [
    { key: 'storage', names: ['storage'], label: 'Storage' },
    { key: 'website', names: ['website'], label: 'Website' },
    { key: 'mysql', names: ['mysql', 'my-sql'], label: 'MySQL' },
]

export const TEMPLATE_EMAIL_EGPLATFORM: TemplateConfigField[] = [
    {
        key: 'account',
        names: ['account', 'mail-account', 'email-account'],
        label: 'Account',
        headerKey: 'mail-account',
    },
    {
        key: 'storage',
        names: ['storage'],
        label: 'Storage',
        headerKey: 'email-storage',
    },
]

export const TEMPLATE_EMAIL_EMAILSERVER: TemplateConfigField[] = [
    {
        key: 'account',
        names: ['account', 'mail-account', 'email-account'],
        label: 'Account',
        headerKey: 'mail-account',
    },
    {
        key: 'storage',
        names: ['storage'],
        label: 'Storage',
        headerKey: 'email-storage',
    },
    {
        key: 'attachment',
        names: ['email-attachment', 'attachment'],
        label: 'Attachment',
        headerKey: 'email-attachment',
    },
    {
        key: 'addon_domain',
        names: ['addon-domain', 'addon_domain'],
        label: 'Addon Domain',
        headerKey: 'addon-domain',
    },
]

export const TEMPLATE_VNIS: TemplateConfigField[] = [
    { key: 'domains', names: ['domains'], label: 'Domains' },
    { key: 'cdn_traffics', names: ['cdn_traffics'], label: 'CDN Traffic' },
    { key: 'cdn_requests', names: ['cdn_requests'], label: 'CDN Requests' },
    { key: 'waf_traffics', names: ['waf_traffics'], label: 'WAF Traffic' },
    { key: 'waf_requests', names: ['waf_requests'], label: 'WAF Requests' },
    {
        key: 'peak_cdn_traffics',
        names: ['peak_cdn_traffics'],
        label: 'Peak CDN Traffic',
    },
    {
        key: 'peak_waf_traffics',
        names: ['peak_waf_traffics'],
        label: 'Peak WAF Traffic',
    },
    {
        key: 'peak_cdn_requests',
        names: ['peak_cdn_requests'],
        label: 'Peak CDN Requests',
    },
    {
        key: 'peak_waf_requests',
        names: ['peak_waf_requests'],
        label: 'Peak WAF Requests',
    },
]

// Default fields for unknown templates
export const TEMPLATE_DEFAULT: TemplateConfigField[] = [
    { key: 'cpu', names: ['cpu', 'vcore', 'vcpu'], label: 'CPU' },
    { key: 'memory', names: ['memory', 'ram'], label: 'Memory' },
    { key: 'disk', names: ['disk', 'ssd', 'hdd', 'storage'], label: 'Storage' },
    { key: 'ip', names: ['ip', 'ip-address', 'ipv4', 'ipv6'], label: 'IP' },
    { key: 'bandwidth', names: ['bandwidth', 'bw'], label: 'Bandwidth' },
]

// All possible fields (union of all templates) - for column definitions
export const ALL_CONFIG_FIELDS: TemplateConfigField[] = [
    // Server
    {
        key: 'server',
        names: ['server', 'processor'],
        label: 'Server/Processor',
    },
    { key: 'cpu', names: ['cpu', 'vcore', 'vcpu'], label: 'CPU' },
    { key: 'memory', names: ['memory', 'ram'], label: 'Memory' },
    { key: 'disk', names: ['disk', 'ssd', 'hdd'], label: 'Disk' },
    { key: 'storage', names: ['storage'], label: 'Storage' },
    { key: 'ip', names: ['ip', 'ip-address', 'ipv4', 'ipv6'], label: 'IP' },
    { key: 'bandwidth', names: ['bandwidth', 'bw'], label: 'Bandwidth' },
    {
        key: 'peak_bandwidth',
        names: ['peak-bandwidth', 'peak_bandwidth', 'peak-bw'],
        label: 'Peak BW',
    },
    // Colocation
    { key: 'u', names: ['u', 'colocation-u'], label: 'U' },
    { key: 'power', names: ['power', 'nguon'], label: 'Power' },
    { key: 'local', names: ['local'], label: 'Local BW' },
    { key: 'global', names: ['global'], label: 'Global BW' },
    // Hosting
    { key: 'website', names: ['website'], label: 'Website' },
    { key: 'mysql', names: ['mysql', 'my-sql'], label: 'MySQL' },
    // Email
    {
        key: 'account',
        names: ['account', 'mail-account', 'email-account'],
        label: 'Account',
    },
    {
        key: 'attachment',
        names: ['email-attachment', 'attachment'],
        label: 'Attachment',
    },
    {
        key: 'addon_domain',
        names: ['addon-domain', 'addon_domain'],
        label: 'Addon Domain',
    },
    // Vnis
    { key: 'domains', names: ['domains'], label: 'Domains' },
    { key: 'cdn_traffics', names: ['cdn_traffics'], label: 'CDN Traffic' },
    { key: 'cdn_requests', names: ['cdn_requests'], label: 'CDN Requests' },
    { key: 'waf_traffics', names: ['waf_traffics'], label: 'WAF Traffic' },
    { key: 'waf_requests', names: ['waf_requests'], label: 'WAF Requests' },
    {
        key: 'peak_cdn_traffics',
        names: ['peak_cdn_traffics'],
        label: 'Peak CDN Traffic',
    },
    {
        key: 'peak_waf_traffics',
        names: ['peak_waf_traffics'],
        label: 'Peak WAF Traffic',
    },
    {
        key: 'peak_cdn_requests',
        names: ['peak_cdn_requests'],
        label: 'Peak CDN Requests',
    },
    {
        key: 'peak_waf_requests',
        names: ['peak_waf_requests'],
        label: 'Peak WAF Requests',
    },
]

// Template mapping for lookup
// Keys must match service_template values from ServiceTemplates (Detail/Templates/index.tsx)
export const TEMPLATE_CONFIG_FIELDS: Record<string, TemplateConfigField[]> = {
    // Server
    'Server.VBM': TEMPLATE_SERVER_VBM,
    'Server.Colocation': TEMPLATE_SERVER_COLOCATION,
    // Cloud
    'Cloud.VPS': TEMPLATE_CLOUD_VPS,
    'Cloud.VCloud': TEMPLATE_CLOUD_VCLOUD,
    'Cloud.Hosting': TEMPLATE_CLOUD_HOSTING,
    // Email
    'Email.Server': TEMPLATE_EMAIL_EMAILSERVER,
    'Email.EGPlatform': TEMPLATE_EMAIL_EGPLATFORM,
    // Vnis
    'Mlytics.Vnis': TEMPLATE_VNIS,
    Vnis: TEMPLATE_VNIS, // backward compatibility
}

/**
 * Get config fields based on service_template
 */
export const getTemplateFields = (
    serviceTemplate?: string
): TemplateConfigField[] => {
    if (!serviceTemplate) return TEMPLATE_DEFAULT

    for (const [key, fields] of Object.entries(TEMPLATE_CONFIG_FIELDS)) {
        if (serviceTemplate.includes(key)) {
            return fields
        }
    }

    return TEMPLATE_DEFAULT
}

/**
 * Get all possible fields for column definitions
 */
export const getAllFields = (): TemplateConfigField[] => ALL_CONFIG_FIELDS

/**
 * Get all field names (flattened) for a template - used for chips filtering
 * Returns all name variations for matching config names
 */
export const getTemplateFieldNames = (serviceTemplate?: string): string[] => {
    const fields = getTemplateFields(serviceTemplate)
    return fields.flatMap((f) => f.names)
}

/**
 * Get default field names for chips filtering
 */
export const getDefaultFieldNames = (): string[] => {
    return TEMPLATE_DEFAULT.flatMap((f) => f.names)
}
